import ApiService from '@/core/services/api.service'
import JwtService from '@/core/services/jwt.service'

import { LOGOUT } from './auth.module.js'
import router from '@/router'
import { eventBus } from '@/main'

// action types
export const GET_WEBHOOK_DATA = 'getWebhookData'
export const UPDATE_WEBHOOK_URL = 'updateWebhookUrl'
export const DISABLE_WEBHOOK = 'disableWebhook'
export const GET_WEBHOOK_JOBS = 'getWebhookJobs'

// mutation types
export const SAVE_WEBHOOK_URL = 'saveWebhookUrl'
export const CLEAR_WEBHOOK_URL = 'clearWebhookUrl'
export const SAVE_WEBHOOK_URL_ERRORS = 'saveWebhookUrlErrors'
export const CLEAR_WEBHOOK_URL_ERRORS = 'clearWebhookUrlErrors'
export const SAVE_WEBHOOK_JOBS_COUNT = 'saveWebhookJobsCount'
export const CLEAR_WEBHOOK_JOBS_COUNT = 'clearWebhookJobsCount'
export const SAVE_WEBHOOK_JOBS = 'saveWebhookJobs'
export const CLEAR_WEBHOOK_JOBS = 'clearWebhookJobs'
export const SAVE_WEBHOOK_JOBS_STATS = 'clearWebhookJobsStats'
export const CLEAR_WEBHOOK_JOBS_STATS = 'clearWebhookJobsStats'
export const SAVE_WEBHOOK_STATUS = 'saveWebhookStatus'

export const DEFAULT_WEBHOOK_JOBS_STATS = {
  limit: 100,
  page: 1,
  total_elements: 0,
  total_pages: 1
}
export const WEBHOOK_JOBS_VOCABULARY = [
  {
    type: 'user_create',
    title: 'New user created'
  },
  {
    type: 'user_delete',
    title: 'User deleted'
  },
  {
    type: 'user_enable',
    title: 'User activated'
  },
  {
    type: 'user_disable',
    title: 'User disabled'
  },
  {
    type: 'user_renew',
    title: 'Changed expiration date for user'
  },
  {
    type: 'user_devices',
    title: 'Changed devices limit for user'
  },
  {
    type: 'service_start',
    title: 'Service started'
  },
  {
    type: 'service_stop',
    title: 'Service has stopped working'
  },
  {
    type: 'webhook_test',
    title: 'Webhook test'
  }
]

const SET_RESPONSE_STATE = 'setResponseState'
export const TOAST_WEBHOOK = 'showToast/ResellerWebhook'

const state = {
  webhookUrl: '',
  webhookUrlErrors: [],
  webhookJobsCount: 0,
  webhookIsEnabled: false,
  webhookJobs: [],
  webhookJobsStats: { ...DEFAULT_WEBHOOK_JOBS_STATS },
  webhookJobsVocabulary: [ ...WEBHOOK_JOBS_VOCABULARY ],

  responses: {
    getWebhookData: false,
    updateWebhookUrl: false,
    disableWebhook: false,
    getWebhookJobs: false,
  }
}

const getters = {
  webhookUrl(state) {
    return state.webhookUrl
  },
  webhookUrlLength(state) {
    return state.webhookUrl.length
  },
  webhookUrlEmpty(state) {
    return state.webhookUrl.length == 0
  },
  webhookUrlErrors(state) {
    return state.webhookUrlErrors
  },
  webhookUrlError: state => field => {
    return state.webhookUrlErrors.filter(err => err.field == field)
  },
  webhookJobsCount(state) {
    return state.webhookJobsCount
  },
  webhookIsEnabled(state) {
    return state.webhookIsEnabled
  },
  webhookJobs(state) {
    return state.webhookJobs
  },
  webhookJobsStats(state) {
    return state.webhookJobsStats
  },
  webhookJobsVocabulary(state) {
    return state.webhookJobsVocabulary
  },
  webhookJobTitle: state => type => {
    const target = state.webhookJobsVocabulary.find(job => job.type === type)
    if (!target) {
      return target.type
    }
    return target.title
  },
  webhookResponses: state => type => {
    return state.responses[type]
  }
}

const actions = {
  [GET_WEBHOOK_DATA](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getWebhookData', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_RESELLER}/${id}/webhook`)
          .then(({ data }) => {
            context.commit(SAVE_WEBHOOK_URL, data.url)
            context.commit(SAVE_WEBHOOK_JOBS_COUNT, data.jobs_wait)
            context.commit(SAVE_WEBHOOK_STATUS, data.enabled)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_WEBHOOK, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getWebhookData', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [UPDATE_WEBHOOK_URL](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'updateWebhookUrl', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.post(`${process.env.VUE_APP_RESELLER}/${id}/webhook`, {
          url: context.getters.webhookUrl
        })
          .then(({ data }) => {
            eventBus.$emit(TOAST_WEBHOOK, {
              message: 'Webhook URL set',
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            const error = err.response.data
            context.commit(SAVE_WEBHOOK_URL_ERRORS, error.errors)
            eventBus.$emit(TOAST_WEBHOOK, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'updateWebhookUrl', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [DISABLE_WEBHOOK](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'disableWebhook', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.delete(`${process.env.VUE_APP_RESELLER}/${id}/webhook`)
          .then(({ data }) => {
            resolve(data)
            eventBus.$emit(TOAST_WEBHOOK, {
              message: 'Webhook disabled',
              variant: 'success'
            })
          })
          .catch(err => {
            eventBus.$emit(TOAST_WEBHOOK, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'disableWebhook', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_WEBHOOK_JOBS](context, { id, method }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getWebhookJobs', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_RESELLER}/${id}/webhook/jobs`)
          .then(({ data }) => {
            context.commit(SAVE_WEBHOOK_JOBS, {
              data: data.list,
              method
            })
            context.commit(SAVE_WEBHOOK_JOBS_STATS, data)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_WEBHOOK, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getWebhookJobs', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
}

const mutations = {
  [SAVE_WEBHOOK_URL](state, data) {
    state.webhookUrl = data ? data : ''
  },
  [CLEAR_WEBHOOK_URL](state) {
    state.webhookUrl = ''
  },
  [SAVE_WEBHOOK_URL_ERRORS](state, errors) {
    state.webhookUrlErrors = errors
  },
  [CLEAR_WEBHOOK_URL_ERRORS](state) {
    state.webhookUrlErrors = []
  },
  [SAVE_WEBHOOK_JOBS_COUNT](state, data) {
    state.webhookJobsCount = data
  },
  [CLEAR_WEBHOOK_JOBS_COUNT](state) {
    state.webhookJobsCount = 0
  },
  [SAVE_WEBHOOK_STATUS](state, status) {
    state.webhookIsEnabled = status
  },
  [SAVE_WEBHOOK_JOBS](state, { data, method }) {
    if (method === 'start') {
      state.webhookJobs = data
    } else if (method === 'load') {
      state.webhookJobs.push(data)
    }
  },
  [CLEAR_WEBHOOK_JOBS](state) {
    state.webhookJobs = []
  },
  [SAVE_WEBHOOK_JOBS_STATS](state, data) {
    /* eslint-disable-next-line no-unused-vars */
    const { list, ...rest } = data
    state.webhookJobsStats = rest
  },
  [CLEAR_WEBHOOK_JOBS_STATS](state) {
    state.webhookJobsStats = { ...DEFAULT_WEBHOOK_JOBS_STATS }
  },
  [SET_RESPONSE_STATE](state, { type, status }) {
    state.responses[type] = status
  },
}

export default {
  state,
  actions,
  mutations,
  getters,
}
